/* ============================================================================
   UI / toast — a short note that appears over the page and leaves by itself (it replaces window.alert).
   Mount ToastProvider once, around the app; any screen then calls `const toast = useToast()` and `toast("Saved.", "success")`.
   Tone is "danger" (the default, for what failed), "success" or "info". A note stays about six seconds, can be dismissed
   sooner, and never moves what is on the page: the stack floats bottom-right, and along the bottom edge on a phone.
   Use a screen's own Alert instead when the failure belongs next to the control that caused it.
   ============================================================================ */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cx } from "./util.js";

const STAY_MS = 6000;
const MAX_SHOWN = 4;
const ToastContext = createContext(null);

function ToastItem({ note, onDismiss }) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(note.id), STAY_MS);
    return () => window.clearTimeout(timer);
  }, [note.id, onDismiss]);
  return (
    <div className={cx("ds-toast", `ds-toast--${note.tone}`)} role={note.tone === "danger" ? "alert" : "status"}>
      <p className="ds-toast__text">{note.message}</p>
      <button type="button" className="ds-toast__close" onClick={() => onDismiss(note.id)} aria-label="Dismiss" title="Dismiss"><X size={16} aria-hidden="true" /></button>
    </div>
  );
}

/* Mounted once at the root (Main.jsx): draws the toasts that useToast() raises. */
export function ToastProvider({ children }) {
  const [notes, setNotes] = useState([]);
  const nextId = useRef(0);
  const dismiss = useCallback((id) => setNotes((all) => all.filter((n) => n.id !== id)), []);
  const toast = useCallback((message, tone = "danger") => {
    if (!message) return;
    nextId.current += 1;
    const note = { id: nextId.current, message: String(message), tone };
    setNotes((all) => [...all.filter((n) => n.message !== note.message), note].slice(-MAX_SHOWN));
  }, []);
  return (
    <ToastContext.Provider value={toast}>
      {children}
      {createPortal(
        <div className="ds-toasts">{notes.map((note) => <ToastItem key={note.id} note={note} onDismiss={dismiss} />)}</div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/* Returns `toast(message, tone)`. Throws when no ToastProvider is above, so a missing mount is found at once and never swallowed. */
export function useToast() {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error("useToast needs a ToastProvider above it (mounted once in Main.jsx).");
  return toast;
}
